import { XIcon } from '@heroicons/react/solid';
import { Alert, Link } from '@yoast/ui-library';
import React from 'react';

import type { Scan } from '../../api/generated/insights.schemas';
import { capitalize } from '../../utils/stringUtils';
import {
  formatNextRetryDate,
  getEarliestNextRetryAfter,
  getNotificationType,
} from './scanRetryUtils';
import useManualRetryScanWithInvalidation from './useManualRetryScanWithInvalidation';

interface IncompleteAnalysisAlertProps {
  failedScans: Scan[];
  brandId: string;
  analysisId: string;
  onDismiss: () => void;
}

const IncompleteAnalysisAlert: React.FC<IncompleteAnalysisAlertProps> = ({
  failedScans,
  brandId,
  analysisId,
  onDismiss,
}) => {
  const notificationType = getNotificationType(failedScans);

  const { mutate: retryScan, isPending: isRetrying } = useManualRetryScanWithInvalidation(
    brandId,
    analysisId,
  );

  if (notificationType === 'none') {
    return null;
  }

  const handleRetry = () => {
    failedScans
      .filter((scan) => scan.canManualRetry)
      .forEach((scan) => {
        retryScan({ brandId, analysisId, scanId: scan.id });
      });
  };

  const dismissButton = (
    <Link
      as="button"
      className="yst-py-1.5 yst-pr-2 yst-no-underline yst-text-inherit yst-font-medium"
      onClick={onDismiss}
      variant="default"
    >
      Dismiss
    </Link>
  );

  const dismissIcon = (
    <div className="yst-ml-auto yst-flex-shrink-0">
      <Link onClick={onDismiss} variant="default">
        <XIcon className="yst-h-4 yst-w-4" />
      </Link>
    </div>
  );

  const renderCanRetryAlert = () => (
    <Alert variant="error">
      <div className="yst-flex yst-w-full">
        <div className="yst-flex-1 yst-space-y-2">
          <p className="yst-mb-0 yst-font-medium">Analysis incomplete</p>
          <p className="yst-mb-0">
            We couldn&apos;t retrieve results for all AI platforms. Please run
            the analysis again to complete the dataset.
          </p>
          <div className="yst-flex yst-gap-4">
            <Link
              as="button"
              className="yst-py-1.5 yst-pr-2 yst-no-underline yst-text-inherit yst-font-medium"
              onClick={handleRetry}
              variant="default"
            >
              {isRetrying ? 'Running...' : 'Run analysis again'}
            </Link>
            {dismissButton}
          </div>
        </div>
        {dismissIcon}
      </div>
    </Alert>
  );

  const renderCooldownAlert = () => {
    const nextRetryAfter = getEarliestNextRetryAfter(failedScans);

    return (
      <Alert variant="info">
        <div className="yst-flex yst-w-full">
          <div className="yst-flex-1 yst-space-y-2">
            <p className="yst-mb-0 yst-font-medium">
              Analysis still incomplete
            </p>
            <p className="yst-mb-0">
              We still can&apos;t retrieve the data. Please wait 24 hours before
              trying again.
              {nextRetryAfter && (
                <>
                  <br />
                  Next retry available: <strong>{capitalize(formatNextRetryDate(nextRetryAfter))}</strong>.
                </>
              )}
            </p>
          </div>
          {dismissIcon}
        </div>
      </Alert>
    );
  };

  const renderExhaustedAlert = () => (
    <Alert variant="error">
      <div className="yst-flex yst-w-full">
        <div className="yst-flex-1 yst-space-y-2">
          <p className="yst-mb-0 yst-font-medium">
            Analysis still incomplete
          </p>
          <p className="yst-mb-0">
            Re-running the analysis didn&apos;t resolve the issue. Please
            contact support so we can investigate the missing data for you.
          </p>
          <div className="yst-flex yst-gap-4">
            <Link
              href="mailto:support@yoast.com"
              className="yst-py-1.5 yst-pr-2 yst-no-underline yst-text-inherit yst-font-medium"
              variant="default"
            >
              Contact support
            </Link>
            {dismissButton}
          </div>
        </div>
        {dismissIcon}
      </div>
    </Alert>
  );

  if (notificationType === 'can_retry') {
    return renderCanRetryAlert();
  }
  if (notificationType === 'cooldown') {
    return renderCooldownAlert();
  }
  return renderExhaustedAlert();
};

export default IncompleteAnalysisAlert;
